import { FC } from 'react';
import { useLocation } from 'react-router';
import { Box, Flex, List, ListItem, Stack } from '@chakra-ui/react';

import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import * as Layout from 'layouts/OfferDetailsLayout';

import ActivateInsuranceButton from '../../components/ActivateInsuranceButton';
import AsideOfferCard from '../../components/AsideOfferCard';
import AskQuestionIntercomButton from '../../components/AskQuestionIntercomButton';
import OfferListIcon from '../../components/OfferListIcon';
import SimilarOffers from '../../components/SimilarOffers';
import useInsuranceQuery from '../../hooks/useInsuranceCoverage';
import offers from '../../offers';
import * as routes from '../../routes';
import { AssuredBy, OfferDetailsFromSource } from '../../types';
import cover from './assets/cover.png';
import locales from './locales';

const OFFER_NAME = 'Legal Assistance';

interface RouteState {
  from?: OfferDetailsFromSource;
}

const LegalAssistanceOffer: FC = () => {
  const { state: routeState } = useLocation<RouteState>();
  const { coverage } = useInsuranceQuery();

  const isCovered = coverage.legalAssistance;

  return (
    <Layout.PageContainer
      trackViewProps={{
        eventProperties: {
          from: routeState?.from,
          offer: OFFER_NAME,
        },
        pageName: 'InsuranceOfferDetails Viewed',
        usePrefix: false,
      }}
    >
      <Layout.BackButton to={routes.offers} />

      <Layout.StickyHeader>
        <Layout.HeaderTitle>{locales.title}</Layout.HeaderTitle>
        {isCovered ? null : <ActivateInsuranceButton offerName={OFFER_NAME} />}
      </Layout.StickyHeader>

      <Layout.Cover ratio={5.2} src={cover} />

      <Layout.ContentWrapper
        aside={
          <Layout.Aside>
            <AsideOfferCard
              assuredBy={AssuredBy.MutuaideAssistance}
              offer={offers.legalAssistance}
            />
            <AskQuestionIntercomButton offer={OFFER_NAME} />
          </Layout.Aside>
        }
      >
        <Stack spacing="space-16">
          {isCovered ? (
            <>
              <Heading>{locales.howTo.title}</Heading>

              {locales.howTo.content.map((text) => (
                <Text key={text}>{text}</Text>
              ))}

              <Box
                as="a"
                background="grey.200"
                borderRadius="8px"
                href={`tel:${locales.howTo.phoneNumber}`}
                maxWidth="320px"
                paddingY="space-16"
                textAlign="center"
              >
                <Heading size="lg" variant="number">
                  {locales.howTo.phoneNumber}
                </Heading>
              </Box>
            </>
          ) : (
            <>
              <Heading paddingBottom="space-16">
                {locales.include.title}
              </Heading>

              <List spacing="space-16">
                {locales.include.items.map((text) => (
                  <ListItem alignItems="baseline" as={Flex} key={text}>
                    <OfferListIcon color="lightBlue.0" name="cross" />
                    <Text>{text}</Text>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Stack>
      </Layout.ContentWrapper>

      <SimilarOffers
        offers={[
          offers.warrantyExtension,
          offers.accidentCoverage,
          offers.mobileScreen,
        ]}
      />
    </Layout.PageContainer>
  );
};

export default LegalAssistanceOffer;
